import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ApiError } from "../api/client";
import { login, login2FA } from "../api/admin";

type Stage = { kind: "password" } | { kind: "tfa"; tempToken: string };

export function LoginPage() {
  const { api, setSession } = useAuth();
  const nav = useNavigate();
  const [stage, setStage] = useState<Stage>({ kind: "password" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      if (stage.kind === "password") {
        const resp = await login(api, email, password);
        if ("requires_2fa" in resp) {
          setStage({ kind: "tfa", tempToken: resp.temp_token });
          setPassword("");
          return;
        }
        if (resp.account.role !== "admin" && resp.account.role !== "owner") {
          setError("admin access required");
          return;
        }
        setSession({ access_token: resp.access_token, refresh_token: resp.refresh_token, account: resp.account });
        nav("/", { replace: true });
      } else {
        const resp = await login2FA(api, stage.tempToken, code);
        if (resp.account.role !== "admin" && resp.account.role !== "owner") {
          setError("admin access required");
          return;
        }
        setSession({ access_token: resp.access_token, refresh_token: resp.refresh_token, account: resp.account });
        nav("/", { replace: true });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "login failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card center-card">
      <h1>Admin sign-in</h1>
      <p className="meta">Restricted to Prism staff.</p>
      <form onSubmit={submit}>
        {stage.kind === "password" ? (
          <>
            <div className="field">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" value={email} autoFocus required
                onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="password">Password</label>
              <input id="password" type="password" value={password} required
                onChange={(e) => setPassword(e.target.value)} />
            </div>
          </>
        ) : (
          <div className="field">
            <label htmlFor="code">2FA code</label>
            <input id="code" type="text" value={code} autoFocus required
              inputMode="numeric" autoComplete="one-time-code"
              onChange={(e) => setCode(e.target.value)} />
          </div>
        )}
        {error && <div className="error">{error}</div>}
        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? "Signing in…" : stage.kind === "password" ? "Sign in" : "Verify"}
        </button>
      </form>
    </div>
  );
}
